import { useState, useEffect, useCallback } from 'react';
import { DateTime } from 'luxon';
import { TranslationService } from '../../../../services/translation.service';
import type IPressRelease from '../../../../models/press/press-release.model';
import PressRelease from '../PressRelease';
import { PressReleasesApiModel } from '../../models/press-releases.api-model';
import type PaginationResultApiModel from '../../../../services/pagination-result.api-model';
import type { IPaginationResult } from '../../../../models/pagination-result.model';
import type { PressReleaseApiModel } from '../../models/press-release.api-model';
import Spinner from '../../../Shared/Spinner';
import { Status } from '../../../../hooks/fetch.hook';
import Alert from '../../../Shared/Alert';

async function getPressReleases(limit: number, offset: number): Promise<IPaginationResult<IPressRelease>> {
  const response = await fetch(`/api/pressinformation/releases?limit=${limit}&offset=${offset}`);
  if (!response.ok) throw new Error(response.statusText);

  const result: PaginationResultApiModel<PressReleaseApiModel> = await response.json();
  return PressReleasesApiModel.mapToBusinessModel(result);
}

function PressReleasesTabPanel({ active = false }: { active: boolean }) {
  const limit = 12;

  const [items, setItems] = useState<IPressRelease[]>([]);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [status, setStatus] = useState({ status: Status.notperformed, updated: DateTime.now() });
  const [currentOffset, setCurrentOffset] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setStatus({ status: Status.loading, updated: DateTime.now() });
      const result = await getPressReleases(limit, currentOffset);
      setCurrentOffset(currentOffset + result.count);
      setItems(items.concat(result.results));
      setShowLoadMore(currentOffset + result.count < result.totalCount);
      setStatus({ status: Status.success, updated: DateTime.now() });
    } catch {
      setStatus({ status: Status.failed, updated: DateTime.now() });
    }
  }, [currentOffset, items]);

  useEffect(() => {
    if (active && !items.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      fetchData();
    }
  }, [items, active, fetchData]);

  async function handleOnMoreClicked() {
    await fetchData();
  }

  return (
    <div className="tab-panel">
      {items && (
        <ol id="press-release-items" className="row p-0" aria-live="polite">
          {items.map((pressRelease) => (
            <li key={pressRelease.id} className="col-12 col-sm-6 col-md-4 mb-4 mb-sm-5">
              <PressRelease pressRelease={pressRelease} />
            </li>
          ))}
        </ol>
      )}
      {showLoadMore && (
        <button className="btn btn-tertiary d-block mx-auto" aria-controls="press-release-items" onClick={handleOnMoreClicked}>
          {TranslationService.get('presspage.more')}
        </button>
      )}
      {status.status === Status.loading && <Spinner delayInMs={300} className="my-3" />}
      <Alert
        show={status.status === Status.failed}
        heading={TranslationService.get('presspage.generalerror')}
        headingTag="h3"
        closable={false}
        className={showLoadMore ? 'mt-3' : ''}
      />
    </div>
  );
}

export default PressReleasesTabPanel;